// src/services/CSVDataLoader.ts
import { Competition, Solution, User, Tag, DataDomain } from '../models';
import { DataUpdate } from './DataService';

/**
 * CSVデータローダーインターフェース
 * CSVファイルからデータを読み込むための機能を定義
 */
export interface CSVDataLoader {
  loadCompetitionsFromCSV(csvContent: string): Promise<Competition[]>;
  loadSolutionsFromCSV(csvContent: string): Promise<Solution[]>;
  loadUsersFromCSV(csvContent: string): Promise<User[]>;
}

/**
 * CSVデータローダーの実装
 */
export class CSVDataLoaderImpl implements CSVDataLoader {
  /**
   * コンペティションデータをCSVから読み込む
   * @param csvContent CSVの内容
   * @returns コンペティションの配列
   */
  async loadCompetitionsFromCSV(csvContent: string): Promise<Competition[]> {
    const lines = csvContent.trim().split('\n');
    const headers = lines[0].split(',');
    
    // 必須ヘッダーの確認
    const requiredHeaders = ['Slug', 'Title', 'Subtitle', 'DeadlineDate'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`CSVに必須ヘッダーがありません: ${missingHeaders.join(', ')}`);
    }
    
    // インデックスの取得
    const slugIndex = headers.indexOf('Slug');
    const titleIndex = headers.indexOf('Title');
    const subtitleIndex = headers.indexOf('Subtitle');
    const deadlineDateIndex = headers.indexOf('DeadlineDate');
    
    // オプションヘッダーのインデックス
    const tagsIndex = headers.indexOf('Tags');
    const dataDomainIndex = headers.indexOf('DataDomain');
    const participantsCountIndex = headers.indexOf('ParticipantsCount');
    
    const competitions: Competition[] = [];
    
    // ヘッダー行をスキップして処理
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      
      if (values.length < headers.length) {
        console.warn(`行 ${i + 1} のデータが不完全です。スキップします。`);
        continue;
      }
      
      const competition: Competition = {
        slug: values[slugIndex],
        title: values[titleIndex],
        subtitle: values[subtitleIndex],
        deadlineDate: values[deadlineDateIndex],
        tags: tagsIndex >= 0 ? this.parseTags(values[tagsIndex]) : [],
        dataDomain: dataDomainIndex >= 0 ? values[dataDomainIndex] : '',
        participantsCount: participantsCountIndex >= 0 ? parseInt(values[participantsCountIndex], 10) || 0 : 0
      };
      
      competitions.push(competition);
    }
    
    return competitions;
  }
  
  /**
   * ソリューションデータをCSVから読み込む
   * @param csvContent CSVの内容
   * @returns ソリューションの配列
   */
  async loadSolutionsFromCSV(csvContent: string): Promise<Solution[]> {
    const lines = csvContent.trim().split('\n');
    const headers = lines[0].split(',');
    
    // 必須ヘッダーの確認
    const requiredHeaders = ['CompetitionSlug', 'Rank', 'Score', 'UserIds', 'Summary'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`CSVに必須ヘッダーがありません: ${missingHeaders.join(', ')}`);
    }
    
    // インデックスの取得
    const competitionSlugIndex = headers.indexOf('CompetitionSlug');
    const rankIndex = headers.indexOf('Rank');
    const scoreIndex = headers.indexOf('Score');
    const userIdsIndex = headers.indexOf('UserIds');
    const summaryIndex = headers.indexOf('Summary');
    
    const solutions: Solution[] = [];
    
    // ヘッダー行をスキップして処理
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      
      if (values.length < headers.length) {
        console.warn(`行 ${i + 1} のデータが不完全です。スキップします。`);
        continue;
      }
      
      const solution: Solution = {
        competitionSlug: values[competitionSlugIndex],
        rank: parseInt(values[rankIndex], 10) || 0,
        score: parseFloat(values[scoreIndex]) || 0,
        userIds: this.parseUserIds(values[userIdsIndex]),
        summary: values[summaryIndex]
      };
      
      solutions.push(solution);
    }
    
    return solutions;
  }
  
  /**
   * ユーザーデータをCSVから読み込む
   * @param csvContent CSVの内容
   * @returns ユーザーの配列
   */
  async loadUsersFromCSV(csvContent: string): Promise<User[]> {
    const lines = csvContent.trim().split('\n');
    const headers = lines[0].split(',');
    
    // 必須ヘッダーの確認
    const requiredHeaders = ['UserId', 'CurrentRank', 'HighestRank'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`CSVに必須ヘッダーがありません: ${missingHeaders.join(', ')}`);
    }
    
    // インデックスの取得
    const userIdIndex = headers.indexOf('UserId');
    const currentRankIndex = headers.indexOf('CurrentRank');
    const highestRankIndex = headers.indexOf('HighestRank');
    
    const users: User[] = [];
    
    // ヘッダー行をスキップして処理
    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      
      if (values.length < headers.length) {
        console.warn(`行 ${i + 1} のデータが不完全です。スキップします。`);
        continue;
      }
      
      const user: User = {
        userId: values[userIdIndex],
        currentRank: parseInt(values[currentRankIndex], 10) || 0,
        highestRank: parseInt(values[highestRankIndex], 10) || 0
      };
      
      users.push(user);
    }
    
    return users;
  }
  
  /**
   * CSVの行を解析する（引用符で囲まれたフィールドを考慮）
   * @param line CSVの行
   * @returns 解析された値の配列
   */
  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  }
  
  /**
   * タグ文字列を解析する
   * @param tagsStr タグ文字列（例: "tag1|tag2|tag3"）
   * @returns タグの配列
   */
  private parseTags(tagsStr: string): string[] {
    return tagsStr.split('|').map(tag => tag.trim()).filter(tag => tag.length > 0);
  }
  
  /**
   * ユーザーID文字列を解析する
   * @param userIdsStr ユーザーID文字列（例: "user1|user2|user3"）
   * @returns ユーザーIDの配列
   */
  private parseUserIds(userIdsStr: string): string[] {
    return userIdsStr.split('|').map(id => id.trim()).filter(id => id.length > 0);
  }
}
